import { Box, Stack, Typography } from '@mui/material'
import { SxProps } from '@mui/system'
import { useMemo } from 'react'

import { useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

export type RankProps = {
  size: 'xSmall' | 'small' | 'medium' | 'large'
  rank: number
  sx?: SxProps
}

export const Rank: Organism<RankProps> = ({ size = 'medium', rank, sx }) => {
  const { t } = useTranslation('myRanking')
  const sizePx = useMemo(() => getSizePixel(size), [size])
  const { backgroundImage, borderColor, bgcolor } = useMemo(() => {
    switch (rank) {
      case 1:
        return {
          borderColor: '#f3af3d',
          backgroundImage:
            'linear-gradient(180deg, #f3cb70 25%, #f3cb70 50%, #f5bc49 50%, #f5bc49 96%)',
        }
      case 2:
        return {
          borderColor: '#b4bbbd',
          backgroundImage:
            'linear-gradient(180deg, #eaebe9 25%, #eaebe9 58%, #d0d0cf 58%, #d0d0cf 96%)',
        }
      case 3:
        return {
          borderColor: '#d28f60',
          backgroundImage:
            'linear-gradient(180deg, #f9dcc3 25%, #f9dcc3 58%, #edc2a0 58%, #edc2a0 96%)',
        }
      default:
        return { bgcolor: 'grey.50', borderColor: 'grey.200' }
    }
  }, [rank])

  return (
    <OrganismFrame
      component={Stack}
      sx={{
        position: 'relative',
        width: sizePx,
        ...sx,
      }}
      justifyContent="center"
      alignItems="center"
    >
      {rank ? (
        <Typography
          variant="h6"
          sx={{
            zIndex: 2,
            WebkitTextStrokeWidth: rank > 3 ? 0 : 0.5,
            WebkitTextStrokeColor: 'black',
          }}
        >
          {rank}
        </Typography>
      ) : (
        <Typography variant="subtitle1" sx={{ zIndex: 2 }}>
          {t('notAttended')}
        </Typography>
      )}
      {!!rank && rank <= 3 && (
        <Box
          sx={{
            zIndex: 0,
            position: 'absolute',
            transform: 'rotate(-45deg)',
            width: `calc(${sizePx}px/(0.707*2))`,
            height: `calc(${sizePx}px/(0.707*2))`,
            backgroundImage,
          }}
        />
      )}
      <Box
        sx={{
          zIndex: 1,
          position: 'absolute',
          borderWidth: 3,
          borderStyle: 'solid',
          borderColor,
          transform: 'rotate(-45deg)',
          width: `calc(${sizePx - (rank && rank <= 3 ? 6 : 0)}px/(0.707*2))`,
          height: `calc(${sizePx - (rank && rank <= 3 ? 6 : 0)}px/(0.707*2))`,
          backgroundImage,
          bgcolor,
        }}
      />
    </OrganismFrame>
  )
}

const getSizePixel = (size: RankProps['size']): number => {
  switch (size) {
    case 'xSmall':
      return 48
    case 'small':
      return 52
    case 'medium':
      return 56
    default:
      return 68
  }
}
